import { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useLocation } from 'react-router-dom';
import AppPage from 'src/components/AppPage';
import AllShopsTable from './AllShopsTable';
import PremiumShopsTable from './PremiumShops';
import ActivePremiumShopsTable from './ActivePremiumShops';
import PaidPremiumShopsTable from './PaidPremiumShops';
import PremiumShopsMessageTable from './AllPremiumShopsMessage';
import { brand } from 'src/theme/brand';
import { usePlanCatalog } from 'src/features/billing/usePlanCatalog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`message-shops-tabpanel-${index}`}
      aria-labelledby={`message-shops-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `message-shops-tab-${index}`,
    'aria-controls': `message-shops-tabpanel-${index}`
  };
}

type ShopView =
  | 'all'
  | 'premium'
  | 'active-premium'
  | 'paid-premium'
  | 'message';

const pageMeta: Record<
  ShopView,
  { title: string; heading: string; description: string }
> = {
  all: {
    title: 'All Shops',
    heading: 'All Shops',
    description: 'Browse and manage every shop on the LumaPOS platform.'
  },
  premium: {
    title: 'Premium Shops',
    heading: 'Premium Shops',
    description: 'Shops on premium plans — review status, notes, and activity.'
  },
  'active-premium': {
    title: 'Active Premium Shops',
    heading: 'Active Premium Shops',
    description: 'Currently active premium shops with an ongoing subscription.'
  },
  'paid-premium': {
    title: 'Paid Premium Shops',
    heading: 'Paid Premium Shops',
    description: 'Premium shops that have completed payment.'
  },
  message: {
    title: 'Message Shops',
    heading: 'Message Shops',
    description:
      'Select shops and send bulk emails or messages across segments.'
  }
};

const AllShops = () => {
  const location = useLocation();
  const [tab, setTab] = useState(0);
  // One tab per plan that has customers, so new plans appear without code changes.
  const { plans } = usePlanCatalog();
  const planTabs = plans.filter((plan) => plan.subscriberCount > 0);

  const view: ShopView = location.pathname.includes('active-premium-shops')
    ? 'active-premium'
    : location.pathname.includes('paid-premium-shops')
    ? 'paid-premium'
    : location.pathname.includes('premium-shops')
    ? 'premium'
    : location.pathname.includes('message')
    ? 'message'
    : 'all';

  const meta = pageMeta[view];

  return (
    <AppPage
      title={meta.title}
      heading={meta.heading}
      description={meta.description}
    >
      {view === 'all' && <AllShopsTable />}
      {view === 'premium' && <PremiumShopsTable />}
      {view === 'active-premium' && <ActivePremiumShopsTable />}
      {view === 'paid-premium' && <PaidPremiumShopsTable />}
      {view === 'message' && (
        <Box>
          <Box sx={{ mb: 1.5, borderBottom: `1px solid ${brand.border}` }}>
            <Tabs
              value={tab}
              onChange={(_, next) => setTab(next)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="All Shops" {...a11yProps(0)} />
              <Tab label="Premium Shops" {...a11yProps(1)} />
              {planTabs.map((plan, i) => (
                <Tab key={plan.code} label={plan.name} {...a11yProps(i + 2)} />
              ))}
            </Tabs>
          </Box>
          <CustomTabPanel value={tab} index={0}>
            <PremiumShopsMessageTable />
          </CustomTabPanel>
          <CustomTabPanel value={tab} index={1}>
            <PremiumShopsMessageTable />
          </CustomTabPanel>
          {planTabs.map((plan, i) => (
            <CustomTabPanel key={plan.code} value={tab} index={i + 2}>
              <PremiumShopsMessageTable packageType={plan.code} />
            </CustomTabPanel>
          ))}
        </Box>
      )}
    </AppPage>
  );
};

export default AllShops;
